import { useState, useEffect, useContext } from "react";
import apiClient from "../../services/apiClient";
import {
  Table,
  Button,
  Form,
  Input,
  Space,
  Popconfirm,
  Typography,
  Divider,
  message,
  Tabs,
  Card,
  Badge,
} from "antd";
import {
  UserAddOutlined,
  DeleteOutlined,
  TeamOutlined,
  CheckOutlined,
  CloseOutlined,
  FormOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import "./AdminDashboard.css";
import dayjs from "dayjs";

const { Title, Text } = Typography;

const initialUsers = [];

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]); // Activated users
  const [pendingUsers, setPendingUsers] = useState([]);
  const [deactivatedUsers, setDeactivatedUsers] = useState([]);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [activeTab, setActiveTab] = useState("activated");
  const [loading, setLoading] = useState(false);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [deactivatedLoading, setDeactivatedLoading] = useState(false);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const { isDark } = useContext(ThemeContext);
  const [pendingActionLoading, setPendingActionLoading] = useState({});
  const [userStats, setUserStats] = useState({
    pendingActionUsers: 0,
    deactivatedUsers: 0,
    totalUsers: 0,
    activeUsers: 0,
    rejectedActions: 0,
    deletedUsers: 0,
  });

  const fetchUsers = async (pageNum = page, size = pageSize) => {
    setLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "";
      const response = await apiClient.get(`${baseUrl}/admin/users`, {
        params: { page: pageNum - 1, size },
      });

      // Check if response has the expected structure with success field
      if (!response.data.success) {
        throw new Error("Failed to fetch users - success field is false");
      }

      const data = response.data.data;
      const mapped = (data.content || []).map((u) => ({
        id: u.id,
        email: u.username,
        role: Array.isArray(u.roles) ? u.roles.join(", ") : u.roles,
        lastPasswordChange: u.lastPasswordChange,
        createdAt: u.createdAt,
        updatedAt: u.updatedAt,
        deactivated: u.deactivated,
        deleted: u.deleted,
      }));
      setUsers(mapped);
      setTotal(data.totalElements || 0);
    } catch (err) {
      // Optionally show error message
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingUsers = async () => {
    setPendingLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "";
      const response = await apiClient.get(`${baseUrl}/admin-action/pending`, {
        params: { page: page - 1, size: pageSize },
      });

      // Check if response has the expected structure with success field
      if (!response.data.success) {
        throw new Error("Failed to fetch pending users - success field is false");
      }

      const data = response.data.data;
      const mapped = (data.content || []).map((u) => ({
        id: u.id,
        email: u.email,
        role: Array.isArray(u.role) ? u.role.join(", ") : u.role,
        lastPasswordChange: u.lastPasswordChange,
        createdAt: u.createdAt,
        updatedAt: u.updatedAt,
        deactivated: u.deactivated,
        deleted: u.deleted,
        actionType: u.actionType,
        initiatedByUsername: u.initiatedByUsername
      }));
      setPendingUsers(mapped);
    } catch (err) {
      // Optionally show error message
    } finally {
      setPendingLoading(false);
    }
  };

  const fetchDeactivatedUsers = async () => {
    setDeactivatedLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "";
      const response = await apiClient.get(`${baseUrl}/admin/users/deactivated`, {
        params: { page: page - 1, size: pageSize },
      });

      // Check if response has the expected structure with success field
      if (!response.data.success) {
        throw new Error("Failed to fetch deactivated users - success field is false");
      }

      const data = response.data.data;
      const mapped = (data.content || []).map((u) => ({
        id: u.id,
        email: u.username,
        role: Array.isArray(u.roles) ? u.roles.join(", ") : u.roles,
        lastPasswordChange: u.lastPasswordChange,
        createdAt: u.createdAt,
        updatedAt: u.updatedAt,
        deactivated: u.deactivated,
        deleted: u.deleted,
      }));
      setDeactivatedUsers(mapped);
    } catch (err) {
      // Optionally show error message
    } finally {
      setDeactivatedLoading(false);
    }
  };

  const fetchDeletedUsers = async () => {
    setDeletedLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "";
      const response = await apiClient.get(`${baseUrl}/admin/users/deleted`, {
        params: { page: page - 1, size: pageSize },
      });

      // Check if response has the expected structure with success field
      if (!response.data.success) {
        throw new Error("Failed to fetch deleted users - success field is false");
      }

      const data = response.data.data;
      const mapped = (data.content || []).map((u) => ({
        id: u.id,
        email: u.username,
        role: Array.isArray(u.roles) ? u.roles.join(", ") : u.roles,
        lastPasswordChange: u.lastPasswordChange,
        createdAt: u.createdAt,
        updatedAt: u.updatedAt,
        deactivated: u.deactivated,
        deleted: u.deleted,
      }));
      setDeletedUsers(mapped);
    } catch (err) {
      // Optionally show error message
    } finally {
      setDeletedLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "";
      const response = await apiClient.get(`${baseUrl}/admin-action/stats`);

      // Check if response has the expected structure with success field
      if (!response.data.success) {
        throw new Error("Failed to fetch user stats - success field is false");
      }

      setUserStats(response.data.data);
    } catch (err) {
      // Optionally show error message
    }
  };

  useEffect(() => {
    fetchUserStats();
  }, []);

  useEffect(() => {
    if (activeTab === "activated") {
      fetchUsers(page, pageSize);
    } else if (activeTab === "pending") {
      fetchPendingUsers();
    } else if (activeTab === "deactivated") {
      fetchDeactivatedUsers();
    } else if (activeTab === "delete") {
      fetchDeletedUsers();
    }
    // eslint-disable-next-line
  }, [activeTab, page, pageSize]);


  const handleAdd = () => {
    navigate("/admin/users/add");
  };

  const handleDeactivateUserStatus = async (data) => {

    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "";
      await apiClient.post(
        `${baseUrl}/admin-action/create`,
        {
          actionType: "DEACTIVATE_USER",
          targetUserId: data.id,
          userRequest: {
            email: data.email,
            role: data.role
          },
        }
      );
      message.success("User status updated successfully");
      fetchUsers(page, pageSize);
      fetchUserStats();
    } catch (err) {
      message.error("Failed to update user status");
    }
  };

  const handleActivateUserStatus = async (data) => {

    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "";
      await apiClient.post(
        `${baseUrl}/admin-action/create`,
        {
          actionType: "ACTIVATE_USER",
          targetUserId: data.id,
          userRequest: {
            email: data.email,
            role: data.role
          },
        }
      );
      message.success("User status updated successfully");
      fetchUsers(page, pageSize);
      fetchUserStats();
    } catch (err) {
      message.error("Failed to update user status");
    }
  };

  const handleDelete = async (data) => {

    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "";
      await apiClient.post(
        `${baseUrl}/admin-action/create`,
        {
          actionType: "DELETE_USER",
          targetUserId: data.id,
          userRequest: {
            email: data.email,
            role: data.role
          },
        }
      );
      message.success("User deleted (soft delete) successfully");
      fetchUsers(page, pageSize);
      fetchUserStats();
    } catch (err) {
      message.error("Failed to delete user");
    }
  };

  // Approve/Reject handlers for pending users
  const handleApproveUser = async (userId) => {
    setPendingActionLoading((prev) => ({ ...prev, [userId]: true }));
    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "";
      const response = await apiClient.post(
        `${baseUrl}/admin-action/approve/${userId}`,
        {}
      );
      message.success(response.data?.message || "User approved successfully");
      fetchPendingUsers();
      fetchUserStats();
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || "Failed to approve user";
      message.error(errorMessage);
    } finally {
      setPendingActionLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const handleRejectUser = async (userId) => {
    setPendingActionLoading((prev) => ({ ...prev, [userId]: true }));
    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "";
      const response = await apiClient.post(
        `${baseUrl}/admin-action/reject/${userId}`,
        {}
      );
      message.success(response.data?.message || "User rejected successfully");
      fetchPendingUsers();
      fetchUserStats();
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || "Failed to reject user";
      message.error(errorMessage);
    } finally {
      setPendingActionLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const handleEdit = (record) => {
    navigate(`/admin/users/edit/${record.id}`, {
      state: { userId: record.id, email: record.email, role: record.role }
    });
  };

  const baseColumns = [
    { 
      title: "Email", 
      dataIndex: "email", 
      key: "email",
      className: "table-header-cell"
    },
    { 
      title: "Role", 
      dataIndex: "role", 
      key: "role",
      className: "table-header-cell"
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      className: "table-header-cell",
      render: (val) => (val ? dayjs(val).format("YYYY-MM-DD HH:mm:ss") : "-"),
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      className: "table-header-cell",
      render: (val) => (val ? dayjs(val).format("YYYY-MM-DD HH:mm:ss") : "-"),
    },
  ];
  const columns = [
    ...baseColumns,
    {
      title: "Actions",
      key: "actions",
      className: "table-header-cell",
      render: (_, record) => {
        if (record.deleted) {
          return null;
        }
        if (record.deactivated) {
          return (
            <Space>
              <Button
                onClick={() => handleActivateUserStatus(record)}
                type="primary"
                size="small"
              >
                Activate
              </Button>
              <Popconfirm
                title="Delete user?"
                onConfirm={() => handleDelete(record)}
              >
                <Button 
                  icon={<DeleteOutlined />} 
                  danger 
                  size="small"
                  style={{ 
                    width: 32, 
                    height: 32, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center' 
                  }}
                />
              </Popconfirm>
            </Space>
          );
        }
        return (
          <Space>
            <Button
              icon={<FormOutlined />}
              onClick={() => handleEdit(record)}
              type="default"
              title="Edit User"
              size="small"
              style={{ 
                width: 40, 
                height: 40, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: 'white',
                borderColor: '#1890ff',
                color: '#1890ff',
                borderRadius: 8
              }}
            />
            <Popconfirm
              title="Delete user?"
              onConfirm={() => handleDelete(record)}
            >
              <Button 
                icon={<DeleteOutlined />} 
                type="default"
                size="small"
                style={{ 
                  width: 40, 
                  height: 40, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: 'white',
                  borderColor: '#ff4d4f',
                  color: '#ff4d4f',
                  borderRadius: 8
                }}
              />
            </Popconfirm>
            <Button
              onClick={() => handleDeactivateUserStatus(record)}
              type="default"
              size="small"
              style={{
                backgroundColor: 'white',
                borderColor: '#8c8c8c',
                color: '#000',
                borderRadius: 8,
                height: 40,
                paddingLeft: 16,
                paddingRight: 16
              }}
            >
              Deactivate
            </Button>
          </Space>
        );
      },
    },
  ];
  const columnsNoActions = baseColumns;

  // Add custom columns for pending tab
  const pendingColumns = [
    ...baseColumns,
    {
      title: "Action Type",
      dataIndex: "actionType",
      key: "actionType",
      render: (val) => val || "-",
    },
    {
      title: "Initiated By",
      dataIndex: "initiatedByUsername",
      key: "initiatedByUsername",
      render: (val) => val || "-",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            size="small"
            loading={!!pendingActionLoading[record.id]}
            onClick={() => handleApproveUser(record.id)}
          >
            Approve
          </Button>
          <Button
            type="default"
            danger
            icon={<CloseOutlined />}
            size="small"
            loading={!!pendingActionLoading[record.id]}
            onClick={() => handleRejectUser(record.id)}
          >
            Reject
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className={`admin-dashboard-container ${isDark ? 'dark' : ''}`}>
      {/* Hero Section */}
      <div
        className="admin-users-hero"
        style={{
          background: 'linear-gradient(90deg, #3AA015 0%, #F49A24 50%, rgba(255, 0, 4, 0.61) 100%)',
          borderRadius: 16,
          padding: "48px 48px 40px 48px",
          marginBottom: 32,
          boxShadow: isDark ? "0 4px 20px rgba(0, 0, 0, 0.3)" : "0 4px 20px rgba(0, 0, 0, 0.08)",
          position: 'relative',
          overflow: 'hidden',
          minHeight: 180,
        }}
      >
        {/* Overlay for better text readability */}
        {isDark && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.5) 100%)',
              borderRadius: 16,
            }}
          />
        )}
        
        {/* Content */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <Title
            level={1}
            style={{
              margin: 0,
              marginBottom: 12,
              fontWeight: 800,
              fontSize: 42,
              color: isDark ? "#ffffff" : "#1a1a1a",
              textShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.3)' : 'none',
            }}
          >
            User Management
          </Title>
          <Text 
            style={{ 
              fontSize: 20, 
              color: isDark ? "rgba(255, 255, 255, 0.9)" : "#4a4a4a",
              textShadow: isDark ? '0 1px 2px rgba(0, 0, 0, 0.3)' : 'none',
              fontWeight: 400,
              lineHeight: 1.4,
            }}
          >
            View, search, and manage all users in your organization.
          </Text>
        </div>
      </div>
      <Divider
        style={{
          margin: 0,
          marginBottom: 32,
          borderColor: isDark ? "#333c4a" : undefined,
        }}
      />
      
      {/* Main Content Card */}
      <Card 
        style={{ 
          borderRadius: 16,
          boxShadow: isDark ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.06)",
          border: isDark ? "1px solid #3a3a4e" : "none",
          background: isDark ? "#2a2a3e" : "#ffffff"
        }}
      >
        {/* Tabs and Add User Button Row */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 24,
            paddingBottom: 16,
            borderBottom: isDark ? "1px solid #3a3a4e" : "1px solid #f0f0f0"
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 0, backgroundColor: isDark ? '#3a3a4e' : '#f5f5f5', borderRadius: 12, padding: 4 }}>
            <div
              onClick={() => setActiveTab("activated")}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '12px 16px',
                borderRadius: 8,
                cursor: 'pointer',
                backgroundColor: activeTab === "activated" ? (isDark ? '#8b5cf6' : '#9FF5F8') : 'transparent',
                transition: 'all 0.2s ease',
                width: 150
              }}
            >
              <TeamOutlined style={{ fontSize: 20, color: isDark ? '#ffffff' : '#333', marginBottom: 4, fontWeight: 'bold' }} />
              <span style={{ fontSize: 15, fontWeight: 700, fontStyle: 'bold', color: isDark ? '#ffffff' : '#333' }}>Active Users</span>
            </div>
            
            <div
              onClick={() => setActiveTab("pending")}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '12px 16px',
                borderRadius: 8,
                cursor: 'pointer',
                backgroundColor: activeTab === "pending" ? (isDark ? '#8b5cf6' : '#9FF5F8') : 'transparent',
                transition: 'all 0.2s ease',
                width: 200
              }}
            >
              <FormOutlined style={{ fontSize: 20, color: isDark ? '#ffffff' : '#333', marginBottom: 4, fontWeight: 'bold' }} />
              <span style={{ fontSize: 15, fontWeight: 700, fontStyle: 'bold', color: isDark ? '#ffffff' : '#333' }}>Pending Actions</span>
            </div>
            
            <div
              onClick={() => setActiveTab("deactivated")}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '12px 16px',
                borderRadius: 8,
                cursor: 'pointer',
                backgroundColor: activeTab === "deactivated" ? (isDark ? '#8b5cf6' : '#9FF5F8') : 'transparent',
                transition: 'all 0.2s ease',
                width: 200
              }}
            >
              <TeamOutlined style={{ fontSize: 20, color: isDark ? '#ffffff' : '#333', marginBottom: 4, fontWeight: 'bold' }} />
              <span style={{ fontSize: 15, fontWeight: 700, fontStyle: 'bold', color: isDark ? '#ffffff' : '#333' }}>Deactivated Users</span>
            </div>
            
            <div
              onClick={() => setActiveTab("delete")}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '12px 16px',
                borderRadius: 8,
                cursor: 'pointer',
                backgroundColor: activeTab === "delete" ? (isDark ? '#8b5cf6' : '#9FF5F8') : 'transparent',
                transition: 'all 0.2s ease',
                width: 150
              }}
            >
              <TeamOutlined style={{ fontSize: 20, color: isDark ? '#ffffff' : '#333', marginBottom: 4, fontWeight: 'bold' }} />
              <span style={{ fontSize: 15, fontWeight: 700, fontStyle: 'bold', color: isDark ? '#ffffff' : '#333' }}>Deleted Users</span>
            </div>
            
            {/* More options icon */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '12px 16px',
                cursor: 'pointer',
                marginLeft: 8
              }}
            >
              <span style={{ fontSize: 18, color: isDark ? '#ffffff' : '#333' }}>⋯</span>
            </div>
          </div>
          
          {/* Add User Button */}
          <Button 
            type="primary" 
            icon={<UserAddOutlined />} 
            onClick={handleAdd}
            style={{
              borderRadius: 8,
              height: 40,
              paddingLeft: 16,
              paddingRight: 16,
              fontWeight: 500
            }}
          >
            Add User
          </Button>
        </div>

        {/* Table Content */}
        {activeTab === "activated" && (
          <Table
            columns={columns}
            dataSource={users}
            rowKey="id"
            pagination={false}
            style={{ width: "100%" }}
            loading={loading}
            size="middle"
            className="custom-table"
          />
        )}
        
        {activeTab === "pending" && (
          <Table
            columns={pendingColumns}
            dataSource={pendingUsers}
            rowKey="id"
            pagination={false}
            style={{ width: "100%" }}
            loading={pendingLoading}
            size="middle"
            className="custom-table"
          />
        )}
        
        {activeTab === "deactivated" && (
          <Table
            columns={columns}
            dataSource={deactivatedUsers}
            rowKey="id"
            pagination={false}
            style={{ width: "100%" }}
            loading={deactivatedLoading}
            size="middle"
            className="custom-table"
          />
        )}
        
        {activeTab === "delete" && (
          <Table
            columns={columnsNoActions}
            dataSource={deletedUsers}
            rowKey="id"
            pagination={false}
            style={{ width: "100%" }}
            loading={deletedLoading}
            size="middle"
            className="custom-table"
          />
        )}
      </Card>

    </div>
  );
};

export default AdminUsers;
